import { createColumnHelper } from '@tanstack/react-table';
import { ContentCard, EnterpriseLink, EnterpriseLinkButton, LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { Table, TableProvider, useTableState } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { useCommanderSelector } from '@mews-commander/utils';
import { WithScope } from '@mews-commander/types';
import { pushUrlToHistory } from '../../../app/actions/common';
import { getCheckedReservationIds } from '../../selectors';
import TaskStateTag from './TaskStateTag';
import { TaskContextMenu } from './TaskContextMenu';
import { useTasksQuery, ExtendedTask } from './query';

const columnHelper = createColumnHelper<ExtendedTask>();
const tableColumns = [
    columnHelper.accessor('name', {
        header: () => lt('Name'),
        cell: (props) => (
            <EnterpriseLink
                to="Task/Detail/$taskId"
                variant="basic"
                params={{ taskId: props.row.original.id }}
                onClick={() => pushUrlToHistory()}
            >
                {props.getValue()}
            </EnterpriseLink>
        ),
        meta: { width: '60%' },
    }),
    columnHelper.accessor('deadlineUtc', {
        header: () => lt('Deadline'),
        cell: (props) => <LocalizedDateTimeFromUtc utcString={props.getValue()} />,
        meta: { width: '20%' },
    }),
    columnHelper.accessor('taskTemporalState', {
        header: () => lt('Status'),
        cell: (props) => <TaskStateTag temporalState={props.getValue()} />,
        meta: { width: '10%' },
    }),
    columnHelper.display({
        id: 'actions',
        header: () => lt('ActionPlural'),
        cell: (props) => (
            <TaskContextMenu id={props.row.original.id} temporalState={props.row.original.taskTemporalState} />
        ),
        meta: { width: '10%' },
    }),
];

interface TableProps {
    reservationIds: string[];
}

const TasksTable = ({ reservationIds }: TableProps) => {
    const tasksQuery = useTasksQuery(reservationIds);
    const [tableState, setTableState] = useTableState<ExtendedTask>();

    return (
        <TableProvider columns={tableColumns} state={tableState} onStateChange={setTableState} as="section">
            <Table
                uniqueRowId="id"
                data={tasksQuery.data ?? []}
                amountOfRows={tasksQuery.data?.length ?? 0}
                loading={tasksQuery.isFetching}
            />
        </TableProvider>
    );
};

export const Tasks = ({ scope }: WithScope) => {
    const reservationIds = useCommanderSelector((state) => getCheckedReservationIds(state, { scope }));
    return (
        <ContentCard
            title={lt('TaskPlural')}
            actions={
                reservationIds.length === 1 && (
                    <EnterpriseLinkButton
                        to="Task/Create"
                        search={{ serviceOrderId: reservationIds[0] }}
                        onClick={() => pushUrlToHistory()}
                    >
                        {lt('CreateTask')}
                    </EnterpriseLinkButton>
                )
            }
        >
            <TasksTable reservationIds={reservationIds} />
        </ContentCard>
    );
};
